import { format, parseISO } from 'date-fns';

import type { SupportRequest, SupportRequestStatus } from '@/types/api';

export const STATUS_LABEL: Record<SupportRequestStatus, string> = {
  received: 'Received',
  in_progress: 'In progress',
  waiting_on_you: 'Waiting on you',
  waiting_on_pharmacy: 'Waiting on pharmacy',
  waiting_on_clinician: 'Waiting on clinician',
  resolved: 'Resolved',
};

const WAITING: SupportRequestStatus[] = [
  'waiting_on_you',
  'waiting_on_pharmacy',
  'waiting_on_clinician',
];

export function isOpen(r: SupportRequest): boolean {
  return r.status !== 'resolved';
}

/** Open requests, the ones waiting on the patient first. */
export function openRequests(all: SupportRequest[]): SupportRequest[] {
  return all
    .filter(isOpen)
    .sort((a, b) => {
      const aw = a.status === 'waiting_on_you' ? 0 : 1;
      const bw = b.status === 'waiting_on_you' ? 0 : 1;
      return aw - bw || b.updated_at.localeCompare(a.updated_at);
    });
}

export function summaryLine(r: SupportRequest): string {
  const who = r.handler_first_name;
  if (r.status === 'resolved') return who ? `Handled by ${who}` : 'Resolved';
  if (r.status === 'waiting_on_you') return who ? `${who} needs your reply` : 'Needs your reply';
  if (r.status === 'received' || !who) return 'Waiting for a teammate';
  return `${who} is on it`;
}

export function when(iso: string): string {
  return format(parseISO(iso), 'MMM d · h:mma');
}

export interface Step {
  label: string;
  meta: string;
  state: 'done' | 'current' | 'todo';
}

/**
 * Four steps: Received → With <name> → <what it's waiting on> → Resolved.
 * The third step names the most recent waiting state the request has been
 * in (or "Waiting on …" generically) so the progress reads like a story.
 */
export function stepsFor(r: SupportRequest): Step[] {
  const assigned = [...r.events].reverse().find((e) => e.kind === 'assigned');
  const lastWait = [...r.events]
    .reverse()
    .find((e) => e.kind === 'status' && e.status && WAITING.includes(e.status));
  const waitStatus = WAITING.includes(r.status) ? r.status : lastWait?.status;
  const handler = r.handler_first_name ?? assigned?.handler_first_name ?? null;

  const resolved = r.status === 'resolved';
  const reached = [
    true,
    Boolean(assigned) || r.status !== 'received',
    Boolean(waitStatus),
    resolved,
  ];
  const currentIndex = resolved
    ? -1
    : WAITING.includes(r.status)
      ? 2
      : r.status === 'received'
        ? 0
        : 1;

  const labels = [
    'Received',
    handler ? `With ${handler}` : 'With a teammate',
    waitStatus ? STATUS_LABEL[waitStatus] : 'Waiting on …',
    'Resolved',
  ];
  const metas = [
    when(r.opened_at),
    assigned ? when(assigned.at) : '',
    lastWait ? when(lastWait.at) : '',
    r.resolved_at ? when(r.resolved_at) : '',
  ];
  return labels.map((label, i) => ({
    label,
    meta: reached[i] ? metas[i] : '',
    state: i === currentIndex ? 'current' : reached[i] && (resolved || i < currentIndex) ? 'done' : 'todo',
  }));
}

/** Timeline copy for a request event rendered inside the Support chat. */
export function eventCopy(
  r: SupportRequest,
  e: SupportRequest['events'][number],
): { title: string; body: string } | null {
  if (e.kind === 'opened') return { title: 'Request received', body: 'A teammate will pick this up.' };
  if (e.kind === 'assigned') {
    return {
      title: e.handler_first_name ? `${e.handler_first_name} picked up your request` : 'A teammate picked up your request',
      body: 'In progress',
    };
  }
  if (e.kind === 'status' && e.status) {
    if (e.status === 'resolved') return { title: 'Resolved', body: `${r.title} is resolved. Reply here if anything’s still off.` };
    return { title: STATUS_LABEL[e.status], body: summaryLine({ ...r, status: e.status }) };
  }
  return null;
}
